'use client';

import BDImage from '@shared/components/elements/bd-image';
import BDButton from '@shared/components/elements/button';
import BDTitle from '@shared/components/elements/title';
import { HSection } from '@shared/components/home/section';
import polishingImage from '@shared/public/images/get-to-know/diamond-polishing.jpg';
import igiCertificationImage from '@shared/public/images/get-to-know/igi_certificate.png';
import isolatingImage from '@shared/public/images/get-to-know/isolating.jpg';
import machineImage from '@shared/public/images/get-to-know/machine.jpg';
import purifyingImage from '@shared/public/images/get-to-know/purifying.jpg';
import receivingImage from '@shared/public/images/get-to-know/receiving.jpg';
import clsx from 'clsx';
import { useEffect, useRef } from 'react';

const steps = [
  {
    title: 'Receiving Your Hair',
    description:
      "We send you a hair collection kit with simple instructions. You'll need to collect at least 6 grams of hair as the carbon source for your Bonded Diamond™.",
    image: receivingImage,
  },
  {
    title: 'Carbon Extraction and Purification',
    description:
      'The hair is cleaned and charred through pyrolysis—a high-heat process that isolates carbon, resulting in a material with 60–90% amorphous carbon.',
    image: isolatingImage,
  },
  {
    title: 'Refining to 99.9% Carbon',
    description:
      'We further purify the carbon at high temperatures, transforming it into a graphite structure with approximately 99.9% purity.',
    image: purifyingImage,
  },
  {
    title: 'Diamond Growth',
    description:
      'The purified graphite is placed in a growth chamber under 5 GPa pressure and 2500°F heat, where it crystallizes into a rough diamond over several weeks.',
    image: machineImage,
  },
  {
    title: 'Polishing, Cutting, Engraving',
    description:
      'Once grown, your diamond is polished, cut to shape and size, and engraved with your custom message (if specified).',
    image: polishingImage,
  },
  {
    title: 'Certification',
    description:
      'Your Bonded Diamond™ is certified by IGI and comes with a certificate of authenticity, verifying quality and the integrity of the process.',
    image: igiCertificationImage,
  },
];

export default function HowToGrow() {
  const stepRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.remove('opacity-0');
            entry.target.classList.add('animate-fadein');
          }
        });
      },
      { threshold: 0.3 }
    );
    const currentStepRefs = [...stepRefs.current];

    currentStepRefs.forEach((ref) => {
      if (ref) {
        observer.observe(ref);
      }
    });

    return () => {
      currentStepRefs.forEach((ref) => {
        if (ref) {
          observer.unobserve(ref);
        }
      });
    };
  }, [stepRefs]);
  return (
    <section id="how-to-grow" className="flex flex-col items-center gap-8 py-12 lg:mx-6">
      <HSection bgClassName="bg-bd-secondary md:pl-auto">
        <BDTitle>How a Bonded Diamond™ is made?</BDTitle>
        <p className="px-2 text-center text-sm md:px-10 lg:text-base">
          Each Bonded Diamond™ is made from the carbon extracted from your joint hair. To create a
          Bonded Diamond™, your hair is received, purified into graphite, and then placed under
          high pressure and temperature to form a rough diamond. The rough diamond is then polished,
          cut, and engraved to meet quality standards. Each Bonded Diamond™ receives an IGI
          certification and a Bonded Diamond™ certificate, verifying its authenticity and the
          process integrity.
        </p>
        <div className="relative grid grid-cols-1 gap-8 gap-x-12 bg-transparent pl-4 pt-8 md:mx-10 md:pl-0 lg:mx-20 lg:pt-16">
          {steps.map((step, index) => (
            <div
              key={index}
              ref={(el) => {
                stepRefs.current[index] = el;
              }}
              className="relative opacity-0"
            >
              {/* <h2 className="ml-8 text-sm capitalize text-slate-100 md:hidden">{step!.title}</h2> */}
              <div
                className={clsx(
                  'group ml-8 grid grid-cols-3 md:ml-0 md:grid-cols-2 md:gap-x-24',
                  index % 2 === 1 && 'md:text-right'
                )}
              >
                <div
                  className={clsx(
                    'md:aspect-16/9 row-start-1 my-auto aspect-square overflow-hidden',
                    index % 2 === 1 && 'col-start-3 md:col-start-2'
                  )}
                >
                  <BDImage
                    src={step!.image!}
                    alt={step!.title}
                    className="h-full w-full object-cover object-center"
                  />
                </div>
                <div
                  className={clsx(
                    'col-span-2 row-start-1 flex flex-col justify-center gap-y-1 sm:gap-y-2 md:col-span-1',
                    index % 2 === 1
                      ? 'mr-4 sm:mr-10 md:mr-0 md:items-end'
                      : 'ml-4 sm:ml-10 md:ml-0 md:items-start'
                  )}
                >
                  <h4 className="font-normal capitalize lg:text-lg">{step!.title}</h4>
                  <p className="text-xs font-light md:text-sm lg:text-base">{step!.description}</p>
                </div>
                {/* decoration */}
                {index !== 0 && (
                  <div
                    key="first-half-line"
                    className="bg-bd-dark absolute -left-1 -top-[1em] h-[50%] w-px -translate-x-1/2 md:left-1/2"
                  ></div>
                )}
                {index !== steps.length - 1 && (
                  <div
                    key="second-half-line"
                    className="bg-bd-dark absolute -left-1 top-[calc(50%+1rem)] h-[50%] w-px -translate-x-1/2 md:left-1/2"
                  ></div>
                )}
                <div className="absolute -left-1 top-1/2 flex h-8 w-8 -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center rounded-full border border-slate-800 md:left-1/2">
                  {index + 1}
                </div>
              </div>
            </div>
          ))}
        </div>
      </HSection>
      <div className="flex w-full flex-row flex-wrap justify-center gap-x-4 gap-y-2">
        <BDButton
          styleType="secondary"
          href="/shop/service"
          className="mx-4 w-full px-12 py-2 md:w-auto"
        >
          Get Started Kit
        </BDButton>
        <BDButton
          styleType="secondary"
          href="/shop/diamonds/bonded"
          className="mx-4 w-full px-12 py-2 md:w-auto"
        >
          Build Your Diamond
        </BDButton>
      </div>
    </section>
  );
}
